import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { api, ApiError } from '../../lib/api';

interface QuizStatus {
  leagueName: string;
  memberStatus: string;
  requirements: string[];
  quiz: {
    questionCount: number;
    minCorrect: number;
    timeLimitMinutes: number;
    maxAttempts: number;
    attemptsUsed: number;
    passed: boolean;
  } | null;
  attempts: Array<{ startedAt: string; correctCount: number | null; passed: boolean | null }>;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
}

interface ActiveAttempt {
  attemptId: string;
  endsAt: string;
  minCorrect: number;
  questions: QuizQuestion[];
}

interface QuizResult {
  correctCount: number;
  total: number;
  minCorrect: number;
  passed: boolean;
  expired: boolean;
  message: string;
}

/**
 * Quiz di ammissione (lineeguida §23) — accessibile:
 * - il tempo è comunicato come ORARIO ASSOLUTO ("hai tempo fino alle 15:42")
 * - il countdown visivo è decorativo (aria-hidden), niente live region al secondo
 * - annunci solo alle soglie: metà tempo, 5 minuti, 1 minuto
 */
export function QuizPage() {
  const { id = '' } = useParams();
  usePageTitle('Quiz di ammissione');
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ['leagues', id, 'quiz-status'],
    queryFn: () => api<QuizStatus>(`/leagues/${id}/quiz/status`),
    enabled: !!id,
  });

  const [attempt, setAttempt] = useState<ActiveAttempt | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<string>('');
  const announcedRef = useRef<Set<string>>(new Set());

  // Countdown decorativo + annunci alle soglie.
  useEffect(() => {
    if (!attempt) return;
    const tick = () => {
      const ms = new Date(attempt.endsAt).getTime() - Date.now();
      if (ms <= 0) {
        setRemaining('Tempo scaduto');
        return;
      }
      const totalMin = Math.floor(ms / 60000);
      const sec = Math.floor((ms % 60000) / 1000);
      setRemaining(`${totalMin}:${String(sec).padStart(2, '0')}`);
      const marks: Array<[string, boolean, string]> = [
        ['5min', totalMin === 4 && sec > 50, 'Mancano 5 minuti alla fine del quiz.'],
        ['1min', totalMin === 0 && sec === 59, 'Manca un minuto alla fine del quiz.'],
      ];
      for (const [key, hit, message] of marks) {
        if (hit && !announcedRef.current.has(key)) {
          announcedRef.current.add(key);
          announce(message, 'assertive');
        }
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [attempt, announce]);

  const start = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await api<ActiveAttempt>(`/leagues/${id}/quiz/start`, { method: 'POST', body: {} });
      setAttempt(res);
      setAnswers({});
      announcedRef.current = new Set();
      const ends = new Date(res.endsAt);
      const time = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(ends);
      announce(
        `Quiz avviato: ${res.questions.length} domande, servono ${res.minCorrect} risposte corrette. Hai tempo fino alle ${time}.`,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Avvio non riuscito.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!attempt) return;
    setError(null);
    setBusy(true);
    try {
      const res = await api<QuizResult>(`/leagues/${id}/quiz/submit`, {
        method: 'POST',
        body: {
          attemptId: attempt.attemptId,
          answers: Object.entries(answers).map(([questionId, answerIndex]) => ({ questionId, answerIndex })),
        },
      });
      setResult(res);
      setAttempt(null);
      announce(res.message, 'assertive');
      queryClient.invalidateQueries({ queryKey: ['leagues', id, 'quiz-status'] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Consegna non riuscita.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  if (status.isLoading) {
    return (
      <AppLayout title="Quiz di ammissione">
        <p role="status" className="mt-4">Caricamento…</p>
      </AppLayout>
    );
  }
  if (!status.data) {
    return (
      <AppLayout title="Quiz di ammissione">
        <p role="alert" className="mt-4 font-medium text-danger">Lega non trovata.</p>
      </AppLayout>
    );
  }

  const s = status.data;
  const endsTime = attempt
    ? new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(new Date(attempt.endsAt))
    : '';

  return (
    <AppLayout title={`Ammissione — ${s.leagueName}`}>
      {/* Nessun quiz né requisiti: dirlo, mai una pagina vuota */}
      {!s.quiz && s.requirements.length === 0 && !attempt && !result && (
        <p className="mt-6 max-w-prose text-lg">
          Questa lega non prevede un quiz di ammissione né requisiti particolari: se sei qui, sei
          già dei nostri. 🐼{' '}
          <Link to={`/leghe/${id}`} className="text-panda-700 underline dark:text-panda-300">
            Torna alla lega
          </Link>
        </p>
      )}

      {/* Requisiti (lineeguida §22) */}
      {s.requirements.length > 0 && (
        <section aria-labelledby="requisiti" className="mt-6 max-w-prose">
          <h2 id="requisiti" className="text-2xl font-bold tracking-tight">Requisiti della lega</h2>
          <ul className="mt-3 list-disc pl-5">
            {s.requirements.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </section>
      )}

      {error && (
        <p role="alert" className="mt-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800">
          {error}
        </p>
      )}

      {/* Esito ultimo tentativo */}
      {result && (
        <section aria-labelledby="esito" className="mt-8 max-w-prose">
          <h2 id="esito" className="text-2xl font-bold tracking-tight">Esito</h2>
          <p role="status" className={`mt-3 text-lg font-semibold ${result.passed ? 'text-success' : 'text-danger'}`}>
            {result.message}
          </p>
          {result.passed && (
            <p className="mt-3">
              <Link to={`/leghe/${id}`} className="button inline-flex items-center justify-center rounded-xl bg-panda-700 px-6 py-2.5 font-semibold text-white no-underline hover:bg-panda-800 dark:bg-panda-600">
                Entra nella lega
              </Link>
            </p>
          )}
        </section>
      )}

      {/* Quiz */}
      {s.quiz && !attempt && !result?.passed && !s.quiz.passed && (
        <section aria-labelledby="quiz-intro" className="mt-8 max-w-prose">
          <h2 id="quiz-intro" className="text-2xl font-bold tracking-tight">Quiz di ammissione</h2>
          <p className="mt-2">
            {s.quiz.questionCount} domande a risposta multipla, tempo massimo{' '}
            {s.quiz.timeLimitMinutes} minuti, servono almeno {s.quiz.minCorrect} risposte corrette.
            Hai usato {s.quiz.attemptsUsed} tentativi su {s.quiz.maxAttempts}.
          </p>
          {s.attempts.length > 0 && (
            <ul className="mt-3 list-disc pl-5 text-ink-700 dark:text-ink-200">
              {s.attempts.map((a, i) => (
                <li key={i}>
                  Tentativo: {a.correctCount ?? 0} corrette — {a.passed ? 'superato' : 'non superato'}
                </li>
              ))}
            </ul>
          )}
          {s.quiz.attemptsUsed < s.quiz.maxAttempts && (
            <div className="mt-4">
              <Button onClick={start} loading={busy} loadingText="Avvio…">
                Inizia il quiz
              </Button>
            </div>
          )}
        </section>
      )}

      {s.quiz?.passed && !result && (
        <p role="status" className="mt-8 text-lg font-semibold text-success">
          Hai già superato il quiz: sei un membro della lega. 🐼
        </p>
      )}

      {attempt && (
        <section aria-labelledby="quiz-in-corso" className="mt-8">
          <h2 id="quiz-in-corso" className="text-2xl font-bold tracking-tight">Quiz in corso</h2>
          <p className="mt-2 text-lg">
            Hai tempo <strong>fino alle {endsTime}</strong>. Risposte date:{' '}
            {Object.keys(answers).length} di {attempt.questions.length}.
          </p>
          <p aria-hidden="true" className="mt-1 font-mono text-xl font-bold">
            ⏱ {remaining}
          </p>

          <ol className="mt-6 flex max-w-2xl flex-col gap-6">
            {attempt.questions.map((q, qi) => (
              <li key={q.id}>
                <fieldset>
                  <legend className="font-bold">
                    Domanda {qi + 1} di {attempt.questions.length}: {q.question}
                  </legend>
                  <div className="mt-2 flex flex-col gap-2">
                    {q.options.map((option, oi) => (
                      <label
                        key={oi}
                        className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border p-3 ${
                          answers[q.id] === oi
                            ? 'border-panda-600 bg-panda-50 dark:bg-ink-900 dark:border-panda-400'
                            : 'border-ink-200 dark:border-ink-800'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          className="h-5 w-5 accent-panda-700"
                          checked={answers[q.id] === oi}
                          onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </li>
            ))}
          </ol>

          <div className="mt-8">
            <Button
              onClick={() => {
                const missing = attempt.questions.length - Object.keys(answers).length;
                if (missing > 0) {
                  announce(
                    `Attenzione: ${missing} domande senza risposta. Puoi comunque consegnare.`,
                    'assertive',
                  );
                }
                submit();
              }}
              loading={busy}
              loadingText="Consegna…"
            >
              Consegna il quiz
            </Button>
          </div>
        </section>
      )}
    </AppLayout>
  );
}
